import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { subscriptionAPI } from '../../Components/services/api';
import AdminLayout from '../layout/AdminLayout';
import { securityService } from './serviceType';

const SubscriptionDetailContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Wrap fetchSubscription in useCallback
  const fetchSubscription = useCallback(async () => {
    try {
      setLoading(true);
      const response = await subscriptionAPI.getAll();
      const found = response.data.subscriptions?.find(
        sub => sub.subscription_id === id || sub.subscriptionId === id
      );
      if (found) {
        setSubscription(found);
      } else {
        toast.error('Subscription not found');
        navigate('/admin?tab=subscriptions');
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      toast.error('Failed to load subscription details');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const handleExtend = async () => {
    const months = prompt('Enter number of months to extend:', '1');
    if (!months) return;
    try {
      setActionLoading(true);
      await subscriptionAPI.extend(id, parseInt(months));
      toast.success(`Subscription extended by ${months} month(s)`);
      window.dispatchEvent(new Event('refreshSubscriptionsData'));
      fetchSubscription();
    } catch (error) {
      console.error('Error extending subscription:', error);
      toast.error('Failed to extend subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this subscription?')) return;
    try {
      setActionLoading(true);
      await subscriptionAPI.cancel(id);
      toast.success('Subscription cancelled successfully');
      window.dispatchEvent(new Event('refreshSubscriptionsData'));
      fetchSubscription();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error('Failed to cancel subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this subscription? This action cannot be undone.')) return;
    try {
      setActionLoading(true);
      await subscriptionAPI.delete(id);
      toast.success('Subscription deleted successfully');
      window.dispatchEvent(new Event('refreshSubscriptionsData'));
      navigate('/admin?tab=subscriptions');
    } catch (error) {
      console.error('Error deleting subscription:', error);
      toast.error('Failed to delete subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      expired: { color: 'bg-red-100 text-red-800', label: 'Expired' },
      cancelled: { color: 'bg-gray-100 text-gray-800', label: 'Cancelled' }
    };
    const config = statusConfig[status] || statusConfig.active;
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="text-center py-12">
        <p className="text-6xl mb-4">🔍</p>
        <p className="text-lg text-gray-600">Subscription not found</p>
        <button
          onClick={() => navigate('/admin?tab=subscriptions')}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          Back to subscriptions
        </button>
      </div>
    );
  }

  const security = securityService(subscription);

  return (
    <div>
      <button
        onClick={() => navigate('/admin?tab=subscriptions')}
        className="flex items-center text-gray-600 hover:text-gray-800 mb-6"
      >
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to Subscriptions
      </button>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Subscription Details</h1>
              <p className="text-sm text-gray-500 mt-1">
                ID: {subscription.subscription_id || subscription.subscriptionId}
              </p>
            </div>
            <div>{getStatusBadge(subscription.status)}</div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Customer Information
              </h3>
              <div className="space-y-2">
                <p>
                  <span className="text-gray-500">Name:</span>{' '}
                  <span className="font-medium">{subscription.customer_name || subscription.customerName}</span>
                </p>
                <p>
                  <span className="text-gray-500">Email:</span>{' '}
                  <span className="font-medium">{subscription.email}</span>
                </p>
                <p>
                  <span className="text-gray-500">Phone:</span>{' '}
                  <span className="font-medium">{subscription.phone_number || subscription.phoneNumber || 'N/A'}</span>
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Subscription Details
              </h3>
              <div className="space-y-2">
                {security && (
                  <p>
                    <span className="text-gray-500">Service:</span>{' '}
                    <span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded text-sm font-medium">{security.label}</span>
                  </p>
                )}
                <p>
                  <span className="text-gray-500">{security ? security.detailLabel : 'Plan'}:</span>{' '}
                  <span className="font-medium">{subscription.selected_plan || subscription.selectedPlan}</span>
                </p>
                {!security && (
                  <>
                    <p>
                      <span className="text-gray-500">OS:</span>{' '}
                      <span className="font-medium">{subscription.selected_os || subscription.selectedOS || 'N/A'}</span>
                    </p>
                    <p>
                      <span className="text-gray-500">Billing Cycle:</span>{' '}
                      <span className="font-medium">{subscription.billing_cycle || subscription.billingCycle}</span>
                    </p>
                    <p>
                      <span className="text-gray-500">Quantity:</span>{' '}
                      <span className="font-medium">{subscription.quantity || 1}</span>
                    </p>
                  </>
                )}
                {(subscription.isCustom || subscription.custom) && (
                  <>
                    <p>
                      <span className="text-gray-500">CPU:</span>{' '}
                      <span className="font-medium">{subscription.cpuCores || 0} vCPU</span>
                    </p>
                    <p>
                      <span className="text-gray-500">RAM:</span>{' '}
                      <span className="font-medium">{subscription.ramGb || 0} GB</span>
                    </p>
                  </>
                )}
                {!security && (
                  <p>
                    <span className="text-gray-500">Amount:</span>{' '}
                    <span className="font-medium text-lg">₵{subscription.total_price || subscription.totalPrice}</span>
                  </p>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Dates
              </h3>
              <div className="space-y-2">
                <p>
                  <span className="text-gray-500">{security ? 'Recorded On' : 'Start Date'}:</span>{' '}
                  <span className="font-medium">{subscription.start_date || subscription.startDate || 'N/A'}</span>
                </p>
                <p>
                  <span className="text-gray-500">{security ? 'Renewal Date' : 'Expiration'}:</span>{' '}
                  <span className="font-medium">{subscription.expiration_date || subscription.expirationDate || 'N/A'}</span>
                </p>
              </div>
            </div>

            {/* Fixed: Added parentheses around the condition */}
            {(subscription.extraStorageNvmeGb > 0 || subscription.extraStorageHddGb > 0) && (
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Extra Storage
                </h3>
                <div className="space-y-2">
                  {subscription.extraStorageNvmeGb > 0 && (
                    <p>
                      <span className="text-gray-500">NVMe:</span>{' '}
                      <span className="font-medium">{subscription.extraStorageNvmeGb} GB</span>
                    </p>
                  )}
                  {subscription.extraStorageHddGb > 0 && (
                    <p>
                      <span className="text-gray-500">SSD:</span>{' '}
                      <span className="font-medium">{subscription.extraStorageHddGb} GB</span>
                    </p>
                  )}
                </div>
              </div>
            )}

            {(subscription.notes) && (
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Add-ons & Notes
                </h3>
                <p className="font-medium whitespace-pre-line">{subscription.notes}</p>
              </div>
            )}

            {subscription.dedicatedIPs > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Dedicated IPs
                </h3>
                <p>
                  <span className="text-gray-500">IP Count:</span>{' '}
                  <span className="font-medium">{subscription.dedicatedIPs}</span>
                </p>
              </div>
            )}
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200 flex flex-wrap gap-3">
            <button
              onClick={handleExtend}
              disabled={actionLoading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
            >
              Extend Subscription
            </button>
            <button
              onClick={handleCancel}
              disabled={actionLoading}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition disabled:opacity-50"
            >
              Cancel Subscription
            </button>
            <button
              onClick={handleDelete}
              disabled={actionLoading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
            >
              Delete Subscription
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SubscriptionDetailPage = () => {
  return (
    <AdminLayout activeTab="subscriptions">
      <SubscriptionDetailContent />
    </AdminLayout>
  );
};

export default SubscriptionDetailPage;